import axios from 'axios';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, onUnmounted, ref, toValue, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import { lowerCase } from '@rygen/rygen-ui/i18n';
import { Loadable } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { Order, RateShop, RateShopStatus, ShipmentSpec } from '@/model';

const RATE_SHOP_POLL_INTERVAL = 500;
const RATE_SHOP_POLL_DURATION_MAX = 5 * 60 * 1000 + RATE_SHOP_POLL_INTERVAL;
const DEFAULT_RATE_SHOP_LOADABLE: Readonly<Loadable<RateShop>> = Object.freeze({
  loading: false,
});

const shipmentSpecsCache: Ref<{ [orderId: number]: ShipmentSpec[] }> = ref({});
const internalShipmentSpecs: Loadable<ShipmentSpec[]> = { loading: false };
const internalRateShops: Ref<{ [shipmentSpecId: number]: Loadable<RateShop> }> =
  ref({});

const rateShopPollingIntervals: {
  [shipmentSpecId: number]: ReturnType<typeof setTimeout> | undefined;
} = {};
const rateShopMaxDurationTimeouts: {
  [shipmentSpecId: number]: ReturnType<typeof setTimeout> | undefined;
} = {};

const getOrCreateRateShopLoadable = (
  shipmentSpecId: number,
): Loadable<RateShop> => {
  internalRateShops.value[shipmentSpecId] = internalRateShops.value[
    shipmentSpecId
  ] ?? { ...DEFAULT_RATE_SHOP_LOADABLE };
  return internalRateShops.value[shipmentSpecId];
};

type LoadingState = Record<number, Set<number | symbol>>;

interface useOrderRateShopResponse {
  addQuoteLoading: (shipmentSpecId: number, key: number | symbol) => void;
  hasShipmentSpecs: ComputedRef<boolean>;
  loadRateShopForShipmentSpec: (
    shipmentSpecId: number,
    isPolling?: boolean,
  ) => Promise<void>;
  loadRelatedShipmentSpecsAndRates: () => Promise<void>;
  quoteLoadingStateByShipmentSpec: ComputedRef<LoadingState>;
  ratesByShipmentSpecs: ComputedRef<{
    [shipmentSpecId: number]: Loadable<RateShop>;
  }>;
  removeQuoteLoading: (shipmentSpecId: number, key: number | symbol) => void;
  shipmentSpecs: ComputedRef<Loadable<ShipmentSpec[]>>;
  updateRateShopForShipmentSpec: (shipmentSpecId: number) => Promise<void>;
}

export const useOrderRateShop = (
  order: MaybeRefOrGetter<Order>,
): useOrderRateShopResponse => {
  const { t } = useI18n();
  const toast = useToast();

  const _quoteLoadingStateByShipmentSpec: Ref<LoadingState> = ref({});

  const quoteLoadingStateByShipmentSpec = computed(
    () => _quoteLoadingStateByShipmentSpec.value,
  );

  const shipmentSpecs = computed(() => {
    const rawOrder = toValue(order);

    return {
      data: rawOrder.id ? shipmentSpecsCache.value[rawOrder.id] : undefined,
      error: internalShipmentSpecs.error,
      loading: internalShipmentSpecs.loading,
    };
  });

  const ratesByShipmentSpecs: ComputedRef<{
    [shipmentSpecId: number]: Loadable<RateShop>;
  }> = computed(() => {
    const entries: [number, Loadable<RateShop>][] =
      shipmentSpecs.value.data
        ?.map((shipmentSpec) => shipmentSpec.id)
        ?.filter(notNull)
        ?.map((id) => {
          const loadable: Loadable<RateShop> = {
            data: internalRateShops.value[id]?.data,
            error: internalRateShops.value[id]?.error,
            loading: internalRateShops.value[id]?.loading ?? false,
          };
          return [id, loadable];
        }) ?? [];
    return Object.fromEntries(entries);
  });

  const hasShipmentSpecs = computed(
    () => !shipmentSpecs.value.data || shipmentSpecs.value.data.length > 0,
  );

  const addQuoteLoading = (shipmentSpecId: number, key: number | symbol) => {
    if (!_quoteLoadingStateByShipmentSpec.value[shipmentSpecId]) {
      _quoteLoadingStateByShipmentSpec.value[shipmentSpecId] = new Set();
    }

    _quoteLoadingStateByShipmentSpec.value[shipmentSpecId].add(key);
  };

  const removeQuoteLoading = (shipmentSpecId: number, key: number | symbol) => {
    _quoteLoadingStateByShipmentSpec.value[shipmentSpecId]?.delete(key);
  };

  const stopRateShopPolling = (shipmentSpecId: number): void => {
    clearInterval(rateShopPollingIntervals[shipmentSpecId]);
    rateShopPollingIntervals[shipmentSpecId] = undefined;
    clearTimeout(rateShopMaxDurationTimeouts[shipmentSpecId]);
    rateShopMaxDurationTimeouts[shipmentSpecId] = undefined;
  };

  const startRateShopPolling = (shipmentSpecId: number): void => {
    // no timeouts or intervals should exist for the current shipment spec
    // just to be safe, clear any existing polling for this shipment spec
    stopRateShopPolling(shipmentSpecId);

    // start polling for rate shop completion
    rateShopPollingIntervals[shipmentSpecId] = setInterval(
      () => loadRateShopForShipmentSpec(shipmentSpecId, true),
      RATE_SHOP_POLL_INTERVAL,
    );

    // set a timeout to stop polling after a certain duration
    rateShopMaxDurationTimeouts[shipmentSpecId] = setTimeout(() => {
      console.warn(
        `Rate shop polling duration exceeded, shipmentSpecId=${shipmentSpecId}`,
      );
      stopRateShopPolling(shipmentSpecId);

      const rateShop = getOrCreateRateShopLoadable(shipmentSpecId);
      rateShop.loading = false;
    }, RATE_SHOP_POLL_DURATION_MAX);
  };

  const stopRateShopPollingForOrder = (orderId: Order['id']): void => {
    if (!orderId) {
      return;
    }

    (shipmentSpecsCache.value[orderId] ?? [])
      .map((shipmentSpec) => shipmentSpec.id)
      .filter(notNull)
      .forEach((shipmentSpecId) => stopRateShopPolling(shipmentSpecId));
  };

  const loadRelatedShipmentSpecsAndRates = async (): Promise<void> => {
    const rawOrder = toValue(order);
    const orderId = rawOrder.id;
    if (!orderId) {
      throw new Error('Missing id for Order');
    }

    internalShipmentSpecs.loading = true;
    internalShipmentSpecs.error = undefined;
    try {
      shipmentSpecsCache.value[orderId] =
        await api.order.findAllRelatedShipmentSpecs(orderId);
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('error loading shipment specs for order', orderId, e);
      internalShipmentSpecs.error = e;
      toast.addError({
        detail: t('errors.loadingParamForParamWithId', {
          param: lowerCase(t('shipmentSpecs.shipmentSpecs')),
          param2: lowerCase(t('orders.orders')),
          id: orderId,
        }),
        error: e,
      });
    } finally {
      internalShipmentSpecs.loading = false;
    }

    if (authorization.hasAuthority('rateShop', 'read')) {
      shipmentSpecsCache.value[orderId]
        .map((shipmentSpec) => shipmentSpec.id)
        .filter(notNull)
        .forEach((id) => loadRateShopForShipmentSpec(id));
    }
  };

  const loadRateShopForShipmentSpec = async (
    shipmentSpecId: number,
    isPolling = false,
  ): Promise<void> => {
    const rateShop = getOrCreateRateShopLoadable(shipmentSpecId);
    rateShop.loading = true;
    rateShop.error = undefined;

    try {
      const incomingRateShop = await api.shipmentSpec.loadRates(shipmentSpecId);

      const isRateShopPollingCompleted =
        rateShop.data?.status === RateShopStatus.COMPLETED ||
        rateShop.data?.status === RateShopStatus.ERROR;

      // Safeguards against in-flight requests mutating rateShop.data after a COMPLETED response has already been stored
      if (isPolling && isRateShopPollingCompleted) {
        stopRateShopPolling(shipmentSpecId);
        rateShop.loading = false;
        return;
      }

      rateShop.data = incomingRateShop;

      const isIncomingRateShopPollingCompleted =
        incomingRateShop.status === RateShopStatus.COMPLETED ||
        incomingRateShop.status === RateShopStatus.ERROR;

      if (isIncomingRateShopPollingCompleted) {
        stopRateShopPolling(shipmentSpecId);
        rateShop.loading = false;
        return;
      }

      const isCurrentlyPollingRateShop =
        !!rateShopPollingIntervals[shipmentSpecId];
      if (isCurrentlyPollingRateShop) {
        // Rate shop polling already active
        return;
      }

      startRateShopPolling(shipmentSpecId);
    } catch (e) {
      console.error(
        'error loading rate shop for shipment spec',
        shipmentSpecId,
        e,
      );
      stopRateShopPolling(shipmentSpecId);
      rateShop.loading = false;
      rateShop.error = e;
    }
  };

  const updateRateShopForShipmentSpec = async (
    shipmentSpecId: number,
  ): Promise<void> => {
    const rateShop = getOrCreateRateShopLoadable(shipmentSpecId);
    rateShop.loading = true;
    rateShop.error = undefined;

    try {
      rateShop.data = await api.shipmentSpec.generateRates(shipmentSpecId);
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error(
        'error loading rate shop for shipment spec',
        shipmentSpecId,
        e,
      );
      rateShop.error = e;
    } finally {
      rateShop.loading = false;
    }
  };

  watch(
    () => toValue(order).id,
    (_newVal, oldVal) => {
      internalShipmentSpecs.data = [];
      internalRateShops.value = {};
      _quoteLoadingStateByShipmentSpec.value = {};
      stopRateShopPollingForOrder(oldVal);
    },
  );

  onUnmounted(() => {
    Object.keys(rateShopPollingIntervals)
      .map((shipmentSpecId) => +shipmentSpecId)
      .forEach((shipmentSpecId) => stopRateShopPolling(shipmentSpecId));
  });

  return {
    addQuoteLoading,
    hasShipmentSpecs,
    loadRelatedShipmentSpecsAndRates,
    loadRateShopForShipmentSpec,
    quoteLoadingStateByShipmentSpec,
    removeQuoteLoading,
    updateRateShopForShipmentSpec,
    ratesByShipmentSpecs,
    shipmentSpecs,
  };
};
